import { Chip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ReactElement } from 'react';

import { useAppSelector } from '../../../store/use-appstate-selector';

export interface PaloLockedBadgeProps {
    /** Whether the partner is locked on the master record today. */
    locked?: boolean;
}

/**
 * The "Gesperrt" mark beside a partner field of an existing document.
 *
 * A document carries its partner as a copy of the master record at the time it
 * was written, so a lock set afterwards is not in the document — it is looked
 * up when the mask opens. The partner stays in the document; the mark says
 * that it may no longer be used (GF 12.10.2026, #2230).
 */
export const PaloLockedBadge = ({ locked }: PaloLockedBadgeProps): ReactElement | null =>
    locked ? <Chip size="small" color="error" label={<FormattedMessage id="app.common.locked" />} /> : null;

/** The lock of the order's supplier, for the mask that edits it. */
export const PaloOrderSupplierLock = (): ReactElement | null => (
    <PaloLockedBadge locked={useAppSelector(state => state.purchaseOrders.entity?.supplierAccount?.isLocked)} />
);

/** The lock of the order's customer. */
export const PaloOrderCustomerLock = (): ReactElement | null => (
    <PaloLockedBadge locked={useAppSelector(state => state.purchaseOrders.entity?.customerAccount?.isLocked)} />
);

/** The lock of the invoice's recipient. */
export const PaloInvoiceRecipientLock = (): ReactElement | null => (
    <PaloLockedBadge
        locked={useAppSelector(state => state.invoiceDetail.overview.stepData.values.recipientAccount?.isLocked)}
    />
);

/** The lock of the invoice's supplier. */
export const PaloInvoiceSupplierLock = (): ReactElement | null => (
    <PaloLockedBadge
        locked={useAppSelector(state => state.invoiceDetail.overview.stepData.values.supplierAccount?.isLocked)}
    />
);

/** The lock of the sales order's customer. */
export const PaloSalesOrderCustomerLock = (): ReactElement | null => (
    <PaloLockedBadge locked={useAppSelector(state => state.salesOrders.entity?.customerAccount?.isLocked)} />
);
